import React from "react";

// Basic placeholder policy. Update with help of legal counsel before relying
// on it for compliance with GDPR, CCPA, FL info-protection statutes, etc.
const LAST_UPDATED = "May 17, 2026";

function PrivacyPolicy() {
  return (
    <div className="settings-section privacy-policy">
      <div className="section-header">
        <h2>Privacy Policy</h2>
        <p className="section-description">Last updated: {LAST_UPDATED}</p>
      </div>

      <div className="card" style={{ maxWidth: 820 }}>
        <div className="card-content" style={{ lineHeight: 1.65 }}>
        <p>
          Poseidon Marine ("we", "us", "our") operates the Chariot platform at
          chariot.poseidonmarine.com (the "Service"). This page explains what
          information we collect, how we use it, and the choices you have. By
          using the Service you agree to the terms below.
        </p>

        <h3>1. Information We Collect</h3>
        <ul>
          <li>
            <strong>Account information.</strong> Name, email, phone number,
            password (stored hashed), and role.
          </li>
          <li>
            <strong>Customer and vessel data.</strong> Customer profiles, boat
            and vehicle records, service history, notes, photos, and inspection
            results that you or our staff enter into the system.
          </li>
          <li>
            <strong>Payment information.</strong> Card payments are processed
            by one or more PCI-compliant payment processors that we use under
            contract (currently Stripe, Square, Intuit QuickBooks Payments,
            Authorize.net, and Chase Paymentech, and may include others over
            time). Full card numbers and CVV codes are sent directly from your
            browser to those processors (or through our PCI-Level-1-certified
            card vault) and are never written to Chariot's own storage. We
            retain only the card brand, the last four digits, expiration
            month/year, and the processor's opaque card identifier so that we
            can charge the card on file for services you have authorized and
            so we can show you which card paid for a given invoice or receipt.
          </li>
          <li>
            <strong>Communications.</strong> Email, SMS, and call records that
            pass through the Service (subject lines, message bodies, recordings,
            transcripts, delivery status).
          </li>
          <li>
            <strong>Usage and device data.</strong> IP address, browser, device,
            log timestamps, and pages visited.
          </li>
        </ul>

        <h3>2. How We Use Your Information</h3>
        <ul>
          <li>To provide, operate, and improve the Service.</li>
          <li>To process payments, issue invoices, and manage memberships.</li>
          <li>To communicate with customers about jobs, appointments, and service updates.</li>
          <li>To detect, investigate, and prevent fraud, abuse, and security incidents.</li>
          <li>To comply with legal obligations.</li>
        </ul>

        <h3>3. Third-Party Service Providers</h3>
        <p>
          We share information only with vendors who help us run the Service,
          and only as needed for them to perform that work. These include:
        </p>
        <ul>
          <li>
            <strong>
              Stripe, Square, Intuit (QuickBooks Payments), Authorize.net, and
              Chase Paymentech (JPMorgan Chase Merchant Services)
            </strong>{" "}
            — payment processing. When you provide a card number to Chariot,
            the card data is transmitted directly to one of these processors
            (or through our PCI-Level-1-certified card vault provider) for
            tokenization and storage on their secure infrastructure. Chariot
            sends and receives only the resulting opaque token, the card brand,
            and the last four digits. We do not see or store the full primary
            account number (PAN) or CVV. We may use any one of these processors
            for a given transaction, and we may add, remove, or switch
            processors over time.
          </li>
          <li>
            <strong>PCI Vault (pcivault.io)</strong> — PCI-DSS Level 1
            certified card vault. Used as a tokenization layer in front of one
            or more payment processors so we can switch processors without
            asking customers to re-enter their card.
          </li>
          <li><strong>Brevo</strong> — transactional email</li>
          <li><strong>Twilio</strong> — SMS messaging</li>
          <li><strong>RingCentral</strong> — phone calls and call analytics</li>
          <li><strong>Google Cloud</strong> — application hosting and database</li>
          <li><strong>Anthropic</strong> — AI-assisted features (only data you submit to those features)</li>
        </ul>
        <p>
          Each payment processor we use is bound by its own PCI-DSS compliance
          program and its own privacy policy. By submitting payment information
          you also agree to the terms and privacy policies of the processor
          handling that transaction:
        </p>
        <ul>
          <li>
            Stripe:{" "}
            <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer">
              stripe.com/privacy
            </a>
          </li>
          <li>
            Square:{" "}
            <a href="https://squareup.com/legal/privacy-no-account" target="_blank" rel="noreferrer">
              squareup.com/legal/privacy-no-account
            </a>
          </li>
          <li>
            Intuit (QuickBooks Payments):{" "}
            <a href="https://www.intuit.com/privacy/statement/" target="_blank" rel="noreferrer">
              intuit.com/privacy/statement/
            </a>
          </li>
          <li>
            Authorize.net:{" "}
            <a href="https://www.authorize.net/about-us/privacy.html" target="_blank" rel="noreferrer">
              authorize.net/about-us/privacy.html
            </a>
          </li>
          <li>
            Chase Paymentech / JPMorgan Chase:{" "}
            <a href="https://www.chase.com/digital/resources/privacy-security" target="_blank" rel="noreferrer">
              chase.com/digital/resources/privacy-security
            </a>
          </li>
        </ul>
        <p>
          We do not sell your personal information. We do not share customer
          data with advertisers.
        </p>

        <h3>4. Cookies and Similar Technologies</h3>
        <p>
          We use cookies that are strictly necessary to keep you logged in
          (an httpOnly refresh-token cookie and a device-trust cookie for
          two-factor authentication). We do not use third-party advertising
          cookies on the Service.
        </p>

        <h3>5. Data Retention</h3>
        <p>
          We retain customer and work-order data for as long as your account is
          active and as needed to provide the Service. We may retain certain
          records longer when required for tax, accounting, or legal reasons.
          On request, we will delete or anonymize information that we are not
          required to keep.
        </p>

        <h3>6. Security</h3>
        <p>
          We use industry-standard safeguards including TLS in transit,
          encryption at rest, scoped access controls, two-factor authentication
          for staff, and regular security review. No system is completely
          secure; if you believe your account has been compromised, contact us
          immediately.
        </p>

        <h3>7. Your Choices</h3>
        <p>You may:</p>
        <ul>
          <li>Access or correct your account information from your profile page.</li>
          <li>Request a copy or deletion of your personal data by contacting us.</li>
          <li>Opt out of non-essential email communications by using the unsubscribe link.</li>
        </ul>
        <p>
          Operational messages (appointment confirmations, invoices, payment
          receipts, security alerts) cannot be turned off while your account is
          active.
        </p>

        <h3>8. Children's Privacy</h3>
        <p>
          The Service is not directed to children under 13, and we do not
          knowingly collect personal information from children.
        </p>

        <h3>9. Changes to This Policy</h3>
        <p>
          We may update this policy from time to time. The "Last updated" date
          at the top reflects the most recent change. If we make material
          changes, we will notify you through the Service or by email.
        </p>

        <h3>10. SMS and Text Messaging</h3>
        <p>
          When you provide your mobile phone number through our website contact
          forms or through the Poseidon Marine customer portal
          (app.poseidonmarine.com), you may consent to receive SMS text messages
          from Poseidon Marine. These messages may include:
        </p>
        <ul>
          <li>Service appointment confirmations and reminders</li>
          <li>Work order and job status updates</li>
          <li>Billing and invoice notifications</li>
          <li>Error notifications related to form submissions</li>
          <li>Responses to your service inquiries</li>
        </ul>
        <p>
          Consent to receive SMS messages is not a condition of purchasing any
          service. Message frequency varies based on your service activity.
          Message and data rates may apply.
        </p>
        <p>
          To opt out of SMS messages at any time, reply <strong>STOP</strong> to
          any message you receive from us. After opting out, you will receive a
          single confirmation message and no further SMS messages will be sent.
        </p>
        <p>
          To request help or information, reply <strong>HELP</strong> to any
          message. You may also contact us at{" "}
          <a href="mailto:support@poseidonmarine.com">support@poseidonmarine.com</a>.
        </p>
        <p>
          We do not share your mobile phone number or SMS consent information
          with third parties for their own marketing purposes.
        </p>

        <h3>11. Contact Us</h3>
        <p>
          Questions or requests about this policy can be sent to{" "}
          <a href="mailto:privacy@poseidonmarine.com">privacy@poseidonmarine.com</a>.
        </p>

        <p style={{ marginTop: 24, fontSize: 13, color: "var(--text-tertiary)" }}>
          This is a starter policy provided for transparency. It is not legal
          advice. Consult an attorney before relying on it for compliance with
          specific regulations (e.g. GDPR, CCPA, Florida information-protection
          statutes).
        </p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
